import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronLeftIcon, ChevronRightIcon, CheckCircle2Icon } from 'lucide-react';
const galleryItems = [{
  id: 1,
  url: 'https://cdn.magicpatterns.com/uploads/asRUqg9mKQwsNMj4eKwkRR/MJ1.png',
  title: 'Professional Lawn Care'
}, {
  id: 2,
  url: 'https://cdn.magicpatterns.com/uploads/ewkjJ4W2LgmK2U5Uk87T6V/MJ2.png',
  title: 'Front Yard Landscaping'
}, {
  id: 3,
  url: 'https://cdn.magicpatterns.com/uploads/ogwkQqW7md7kgxwRPBNFyM/MJ3.png',
  title: 'Mulch & Flower Beds'
}, {
  id: 4,
  url: 'https://cdn.magicpatterns.com/uploads/5jtjpM4QJsp3RaayAMAnbc/MJ4.png',
  title: 'Concrete Slab Work'
}, {
  id: 5,
  url: 'https://cdn.magicpatterns.com/uploads/c66dtTRWQuFbSDyuHNJzpa/MJ5.png',
  title: 'Edging & Rock Borders'
}, {
  id: 6,
  url: 'https://cdn.magicpatterns.com/uploads/kbLJEg1sUGE1pH2p84TcdC/heylloyd-image-1774020338433.png',
  title: 'Stone Paver Walkway'
}, {
  id: 7,
  url: 'https://cdn.magicpatterns.com/uploads/sGeoEChPkzAhRcv1k5VJY3/IA3.png',
  title: 'Raised Flower Bed & Rock Work'
}, {
  id: 8,
  url: 'https://cdn.magicpatterns.com/uploads/kD5VZ12joxpCHoqqEfdzfz/heylloyd-image-1773555335166.png',
  title: 'Curved Pathway & Landscape Lighting'
}, {
  id: 9,
  url: 'https://cdn.magicpatterns.com/uploads/gh8k6SH6A7Y7eb1fd9ZcUD/panchos2.png',
  title: 'Garden Design & Planting'
}];
export function Gallery() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [direction, setDirection] = useState(0);
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape' && isLightboxOpen) setIsLightboxOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, isLightboxOpen]);
  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isLightboxOpen]);
  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setDirection(1);
    setCurrentIndex(prev => (prev + 1) % galleryItems.length);
  };
  const handlePrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setDirection(-1);
    setCurrentIndex(prev => (prev - 1 + galleryItems.length) % galleryItems.length);
  };
  const handleThumbnailClick = (index: number) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  return <section id="gallery" className="py-32 bg-white relative z-10 overflow-hidden" data-id="element-74">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-75">
        <div className="text-center mb-16 relative" data-id="element-76">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-77">
            <div className="flex items-center justify-center gap-2 mb-4" data-id="element-78">
              <span className="text-gold font-bold tracking-widest uppercase text-sm" data-id="element-79">
                Portfolio
              </span>
              <div className="flex items-center gap-1.5 bg-forest-light/10 text-forest px-3 py-1 rounded-full text-xs font-semibold" data-id="element-80">
                <CheckCircle2Icon className="w-3.5 h-3.5 text-gold" data-id="element-81" />
                9+ Projects Featured
              </div>
            </div>

            <h2 className="text-4xl md:text-6xl font-bold text-forest mb-6 font-serif" data-id="element-82">
              Our Work
            </h2>
            <div className="w-24 h-1.5 bg-gold mx-auto rounded-full mb-8" data-id="element-83"></div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light" data-id="element-84">
              Take a look at some of our recent landscaping and hardscaping
              projects across Washington State.
            </p>
          </motion.div>
        </div>

        <div className="max-w-4xl mx-auto" data-id="element-85">
          {/* Main Carousel Image */}
          <div className="relative w-full aspect-[4/3] md:aspect-[16/9] rounded-3xl overflow-hidden shadow-2xl mb-8 group bg-gray-100" data-id="element-86">
            <AnimatePresence initial={false} custom={direction} data-id="element-87">
              <motion.img key={currentIndex} src={galleryItems[currentIndex].url} alt={galleryItems[currentIndex].title} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 300,
                damping: 30
              },
              opacity: {
                duration: 0.2
              }
            }} className="absolute inset-0 w-full h-full object-cover cursor-pointer" onClick={() => setIsLightboxOpen(true)} data-id="element-88" />
            </AnimatePresence>

            {/* Navigation Arrows */}
            <button onClick={handlePrev} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg text-gray-800 transition-all z-10 opacity-0 group-hover:opacity-100 focus:opacity-100" data-id="element-89">
              <ChevronLeftIcon className="w-6 h-6" data-id="element-90" />
            </button>
            <button onClick={handleNext} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg text-gray-800 transition-all z-10 opacity-0 group-hover:opacity-100 focus:opacity-100" data-id="element-91">
              <ChevronRightIcon className="w-6 h-6" data-id="element-92" />
            </button>

            {/* Dot Indicators */}
            <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2 z-10" data-id="element-93">
              {galleryItems.map((_, idx) => <button key={idx} onClick={() => handleThumbnailClick(idx)} className={`w-2.5 h-2.5 rounded-full transition-all shadow-sm ${idx === currentIndex ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/80'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-94" />)}
            </div>

            {/* Title Overlay */}
            <div className="absolute top-0 left-0 right-0 p-6 bg-gradient-to-b from-black/60 to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-95">
              <h3 className="text-white text-xl font-medium drop-shadow-md" data-id="element-96">
                {galleryItems[currentIndex].title}
              </h3>
            </div>
          </div>

          {/* Thumbnail Grid */}
          <div className="grid grid-cols-3 gap-4" data-id="element-97">
            {galleryItems.map((item, idx) => <button key={item.id} onClick={() => handleThumbnailClick(idx)} className={`relative aspect-square rounded-2xl overflow-hidden transition-all duration-300 ${idx === currentIndex ? 'ring-4 ring-forest ring-offset-2 scale-[0.98]' : 'hover:opacity-80 hover:scale-[1.02]'}`} data-id="element-98">
                <img src={item.url} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" data-id="element-99" />
                {idx !== currentIndex && <div className="absolute inset-0 bg-black/10 transition-opacity hover:bg-transparent" data-id="element-100" />}
              </button>)}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence data-id="element-101">
        {isLightboxOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-[100] flex items-center justify-center bg-forest-dark/95 backdrop-blur-xl p-4 sm:p-8" onClick={() => setIsLightboxOpen(false)} data-id="element-102">
            <button className="absolute top-6 right-6 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors z-50" onClick={() => setIsLightboxOpen(false)} data-id="element-103">
              <XIcon className="w-6 h-6" data-id="element-104" />
            </button>

            <button className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-4 rounded-full transition-colors z-50" onClick={handlePrev} data-id="element-105">
              <ChevronLeftIcon className="w-8 h-8" data-id="element-106" />
            </button>

            <button className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-4 rounded-full transition-colors z-50" onClick={handleNext} data-id="element-107">
              <ChevronRightIcon className="w-8 h-8" data-id="element-108" />
            </button>

            <motion.div key={currentIndex} initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: -20
        }} transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} className="relative max-w-6xl w-full max-h-[85vh] flex flex-col items-center" onClick={e => e.stopPropagation()} data-id="element-109">
              <img src={galleryItems[currentIndex].url} alt={galleryItems[currentIndex].title} className="w-full h-full object-contain max-h-[75vh] rounded-2xl shadow-2xl" data-id="element-110" />
              <div className="mt-6 text-center" data-id="element-111">
                <h3 className="text-3xl font-bold text-white font-serif mb-2" data-id="element-112">
                  {galleryItems[currentIndex].title}
                </h3>
                <p className="text-gold font-medium tracking-widest uppercase text-sm" data-id="element-113">
                  Project {currentIndex + 1} of {galleryItems.length}
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}